"use client";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CacheKeys } from "../../constants/CacheKeys";
import { VsaTableColumnDefinition } from "../../constants/DataGridColumns";
import { SelectionPersistenceKeys } from "../../constants/SelectionPersistenceKeys";
import { useGetActivityFields } from "../../hooks/useActivities";
import {
  useGetDocumentFields,
  useGetDocumentQuickEditFields,
  useUpdateToolDrafts,
} from "../../hooks/useTools";
import { ApiEndpoints } from "../../models/ApiEndpoints";
import { ClientRoutes } from "../../models/ClientRoutes";
import { DocumentGridViewItem } from "../../models/DocumentGridViewItem";
import { FieldValue } from "../../models/Field";
import { UpdateToolsMetadata, UpdateToolsRequest } from "../../models/Tool";
import {
  getDocumentGridColumnsFromColumnOrder,
  mapDocumentGridDraftItems,
} from "../../services/DocumentGridViewService";
import { ReportType } from "../../services/ReportService";
import {
  PaginatedList,
  PaginatedListEditingOptions,
} from "../list/PaginatedList";

/**
 * Draft documents list backed by the DocumentGridView API. Field values are
 * pre-merged on the server and flattened client-side into the Tool-shaped
 * `fieldValues` so the existing quick-edit and save pipeline can be reused
 * without changes.
 */
const DraftDocumentList = () => {
  const { t } = useTranslation();

  const { mutateAsync: updateTools, isPending: updateToolsPending } =
    useUpdateToolDrafts();

  const { data: documentViewDefinition, isLoading: documentFieldsLoading } =
    useGetDocumentFields();
  const { data: quickEditViewDefinition } = useGetDocumentQuickEditFields();

  const {
    data: activityViewDefinition,
    isLoading: activityViewDefinitionLoading,
  } = useGetActivityFields();

  const columns = useMemo<
    VsaTableColumnDefinition<DocumentGridViewItem>[]
  >(() => {
    if (
      documentFieldsLoading ||
      !documentViewDefinition ||
      activityViewDefinitionLoading ||
      !activityViewDefinition
    ) {
      return [];
    }

    return getDocumentGridColumnsFromColumnOrder(
      documentViewDefinition.standardFields,
      documentViewDefinition.fields,
      activityViewDefinition.fields,
      documentViewDefinition.draftsFieldOrder,
      true
    );
  }, [
    documentViewDefinition,
    documentFieldsLoading,
    activityViewDefinition,
    activityViewDefinitionLoading,
  ]);

  /**
   * Flattens the API response into the Tool-shaped `fieldValues` expected by
   * `GenericReactGrid` and the draft save mapping. Activity-inherited fields
   * are intentionally excluded so they cannot be round-tripped on save.
   */
  const transformItems = (items: DocumentGridViewItem[]) =>
    mapDocumentGridDraftItems(items, documentViewDefinition?.fields ?? []);

  /**
   * Maps a quick-edited document grid row to the draft update request shape.
   */
  const mapItemToRequest = (
    item: DocumentGridViewItem
  ): UpdateToolsMetadata => {
    const newFieldsWithValues: Record<string, FieldValue> = {};

    if (item.fieldValues && quickEditViewDefinition?.fields) {
      quickEditViewDefinition.fields.forEach((field) => {
        if (field.type === 0) {
          const tagFieldValue = item.fieldValues?.tags[field.uniqueName];
          if (tagFieldValue) {
            newFieldsWithValues[field.uniqueName] = tagFieldValue.map(
              (tag) => tag.id
            );
          }
        } else if (field.type === 3) {
          const userFieldValue = item.fieldValues?.users[field.uniqueName];
          if (userFieldValue) {
            if (
              Array.isArray(userFieldValue) &&
              typeof userFieldValue[0] === "string"
            ) {
              newFieldsWithValues[field.uniqueName] = userFieldValue;
            } else {
              newFieldsWithValues[field.uniqueName] = (
                userFieldValue as { id: string }[]
              )
                .map((u) => u.id)
                .filter(Boolean);
            }
          }
        } else {
          const booleanFieldValue =
            item.fieldValues?.booleans[field.uniqueName];
          if (booleanFieldValue !== undefined && booleanFieldValue !== null) {
            newFieldsWithValues[field.uniqueName] = booleanFieldValue;
          }
        }
      });
    }

    return {
      versionId: item.versionId,
      title: item.title,
      webUrl: item.webUrl,
      fieldsWithValues: newFieldsWithValues,
    };
  };

  const onClickSave = async (itemsToSave: DocumentGridViewItem[]) => {
    const request: UpdateToolsRequest = {
      toolMetadata: itemsToSave.map(mapItemToRequest),
    };
    await updateTools(request);
  };

  const editingOptions: PaginatedListEditingOptions<DocumentGridViewItem> = {
    createFormRoute: `${ClientRoutes.ToolCreate}?toolType=Document`,
    allowQuickEdit: true,
    showFileNameColumn: true,
    onSave: onClickSave,
    savePending: updateToolsPending,
    viewDefinition: quickEditViewDefinition,
    entityType: "tool",
    toolType: "Document",
  };

  return (
    <PaginatedList<DocumentGridViewItem>
      apiEndpoint={ApiEndpoints.DocumentGridViewDrafts}
      columns={columns}
      defaultSortAscending={false}
      defaultSortColumnName={"modified"}
      searchPlaceholder={t("searchTool")}
      cacheKey={CacheKeys.Document.DocumentDraftList}
      filtersEndpoint={ApiEndpoints.ToolDocumentFilters}
      editingOptions={editingOptions}
      postQueryTransformation={transformItems}
      reportType={ReportType.DraftDocuments}
      storageKey="documents:drafts"
      libraryFilterScope={{ listType: "document" }}
      selectionPersistenceKey={SelectionPersistenceKeys.DocumentsDrafts}
    />
  );
};

export default DraftDocumentList;
